/**
 * Django Auth Client
 * Axios client configured for Django REST Framework authentication
 */

import axios from 'axios';

const DJANGO_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Django auth client with token management
 */
const djangoClient = axios.create({
  baseURL: DJANGO_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Request interceptor - adds auth token and tenant context
 */
djangoClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // SHR-03: authSlice (the source of truth for the current session's
    // tenant -- every login/refresh path) only ever writes tenant_id to
    // sessionStorage, never localStorage; localStorage.tenant_id is only
    // ever set once, during signup, and can go stale across later logins
    // to a different tenant on the same browser. Prefer sessionStorage
    // (matches baseQueryWithReauth's already-correct behavior), fall back
    // to localStorage only for the narrow window before a session write
    // has happened.
    const tenantId = sessionStorage.getItem('tenant_id') || localStorage.getItem('tenant_id');
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    // Add correlation ID for request tracing
    config.headers['X-Correlation-ID'] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Shared in-flight refresh promise so concurrent 401s trigger a single
 * refresh call instead of a refresh storm (SHR-01/SHR-02).
 */
let refreshPromise = null;

const REFRESH_URL = `${DJANGO_API_URL}/api/v1/auth/token/refresh/`;

/**
 * Response interceptor - handles token refresh
 *
 * Always attempts a refresh on 401, even when no refresh token is stored in
 * localStorage: cookie-authenticated sessions (djangoClient sends
 * withCredentials: true) carry the refresh token as an httpOnly cookie, so
 * the backend can refresh from the cookie alone (SHR-01). We log out only
 * when the refresh endpoint itself rejects the session (401/403) — a
 * network error or 5xx from the refresh call does not clear auth state or
 * force a redirect (SHR-02).
 */
djangoClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 - try token refresh (skip the refresh endpoint itself)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/token/refresh/')
    ) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      const body = refreshToken ? { refresh: refreshToken } : {};

      if (!refreshPromise) {
        refreshPromise = axios.post(REFRESH_URL, body, { withCredentials: true }).finally(() => {
          refreshPromise = null;
        });
      }

      try {
        const response = await refreshPromise;

        const { access, refresh } = response.data;
        if (access) {
          localStorage.setItem('access_token', access);
        }
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return djangoClient(originalRequest);
      } catch (refreshError) {
        const refreshStatus = refreshError?.response?.status;

        // Only a rejected refresh (401/403) means the session is actually
        // over. Network errors / timeouts / 5xx leave auth state intact so
        // a transient outage does not force every tab into a logout.
        if (refreshStatus === 401 || refreshStatus === 403) {
          clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Clear all auth data from localStorage
 */
export const clearAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('user_data');
  localStorage.removeItem('permissions');
};

/**
 * Parse Django REST Framework error response
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
export const parseDjangoError = (error) => {
  if (!error.response) {
    return 'Network error. Please check your connection.';
  }

  const { status, data } = error.response;

  // Handle validation errors (400)
  if (status === 400 && data) {
    if (typeof data === 'object') {
      // Single top-level message shapes (e.g. { error: "..." } or { detail: "..." }
      // from a custom view) are not per-field validation errors — return them
      // directly rather than letting the field-mapping loop below treat the
      // "error"/"detail" key as a form field name (produces "error: <message>").
      if (typeof data.detail === 'string') return data.detail;
      if (typeof data.error === 'string') return data.error;
      if (typeof data.message === 'string') return data.message;

      const messages = [];
      Object.entries(data).forEach(([field, errors]) => {
        if (Array.isArray(errors)) {
          messages.push(`${field}: ${errors.join(', ')}`);
        } else if (typeof errors === 'string') {
          messages.push(`${field}: ${errors}`);
        }
      });
      if (messages.length > 0) {
        return messages.join('; ');
      }
    }
    return data.detail || data.message || 'Validation error';
  }

  // Handle common HTTP errors
  const errorMessages = {
    401: 'Authentication required. Please log in.',
    403: 'You do not have permission to perform this action.',
    404: 'Resource not found.',
    409: 'Conflict: The resource already exists.',
    422: 'Invalid data provided.',
    500: 'Server error. Please try again later.',
    502: 'Service temporarily unavailable.',
    503: 'Service temporarily unavailable.',
  };

  return data?.detail || data?.message || errorMessages[status] || 'An error occurred';
};

export default djangoClient;
